import { useMemo, useState } from 'react';
import { Phone, Mail, Calendar, FileText, Users, Clock, CheckCircle, Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Activity } from '@/types/crm';
import { formatDate } from '@/lib/utils';
import { AttachmentSection } from '@/components/attachments/AttachmentSection';

interface ActivityTimelineProps {
  activities: Activity[];
  loading?: boolean;
}

const activityIcons: Record<Activity['type'], React.ElementType> = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  task: FileText,
  note: FileText,
};

const activityColors: Record<Activity['type'], string> = {
  call: 'bg-blue-100 text-blue-600',
  email: 'bg-green-100 text-green-600',
  meeting: 'bg-purple-100 text-purple-600',
  task: 'bg-orange-100 text-orange-600',
  note: 'bg-gray-100 text-gray-600',
};

export function ActivityTimeline({ activities, loading }: ActivityTimelineProps) {
  const [expandedActivity, setExpandedActivity] = useState<string | null>(null);
  
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [activities]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 animate-pulse">
            <div className="w-10 h-10 rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 bg-muted rounded" />
              <div className="h-3 w-full bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sortedActivities.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>No activities yet</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
      <div className="space-y-6">
        {sortedActivities.map((activity) => {
          const Icon = activityIcons[activity.type] || FileText;
          const colorClass = activityColors[activity.type] || 'bg-gray-100 text-gray-600';
          
          return (
            <div key={activity.id} className="relative flex gap-4 pl-2">
              <div className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full ${colorClass}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium">{activity.subject}</h4>
                  <Badge variant="outline" className="capitalize">{activity.type}</Badge>
                  {activity.is_completed && (
                    <Badge variant="secondary" className="gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Completed
                    </Badge>
                  )}
                </div>
                {activity.description && (
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                    {activity.description}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(activity.created_at)}
                  </span>
                  {activity.due_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Due: {formatDate(activity.due_date)}
                    </span>
                  )}
                  <button
                    onClick={() => setExpandedActivity(expandedActivity === activity.id ? null : activity.id)}
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <Paperclip className="h-3 w-3" />
                    Attachments
                  </button>
                </div>
                {expandedActivity === activity.id && (
                  <div className="mt-3 pt-3 border-t">
                    <AttachmentSection entityType="activity" entityId={activity.id} compact />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
